import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { RequestService } from 'src/app/services/request.service';
import { KeycloakService } from 'keycloak-angular';
import { Chart, ChartConfiguration } from 'chart.js';
import '@sgratzl/chartjs-chart-boxplot';
import { ViolinController, Violin } from '@sgratzl/chartjs-chart-boxplot';
import { MatSelectChange } from '@angular/material/select';

Chart.register(ViolinController, Violin);

@Component({
  selector: 'app-soil-home',
  templateUrl: './soil-home.component.html',
  styleUrls: ['./soil-home.component.scss']
})
export class SoilHomeComponent implements OnInit {

  loggedIn: boolean = false;
  chartOptions: { title: string; tooltip: string; config: ChartConfiguration }[] = [];
  soilData: any;
  countries: any = [];
  managementTypes: any = ['Organic', 'Conventional', 'Monoculture', 'Mixture'];
  parameters: { key: string; label: string; unit: string; tooltip: string }[] = [
    { key: 'ph', label: 'pH', unit: ' ', tooltip: 'Soil pH is a measure of the acidity or basicity of a soil.' },
    { key: 'bulk_density', label: 'Bulk Density', unit: 'g/cm³', tooltip: 'Bulk density describes the mass of soil per unit volume.' },
    { key: 'toc', label: 'Total Soil Organic Carbon', unit: 'g/kg', tooltip: 'Soil organic carbon is crucial for soil fertility.' },
    { key: 'whc', label: 'Water Holding Capacity (WHC)', unit: '% (gravimetric)', tooltip: 'Water holding capacity describes the soil’s ability to retain moisture.' },
    { key: 'tan', label: 'Available Nitrogen', unit: 'mg N / kg soil', tooltip: 'Available nitrogen is a key nutrient for plant growth.' },
    { key: 'nematodes_abundance', label: 'Nematode Abundance', unit: 'total number of nematodes per 100 g fresh weight soil', tooltip: 'Nematodes are microscopic organisms that are indicators of soil health.' },
    { key: 'collembola_abundance', label: 'Collembola Abundance', unit: 'number of genus richness collembola per 100 g fresh weight soil', tooltip: 'Collembola are soil invertebrates involved in organic matter decomposition.' }
  ];

  selectedParameter: { key: string; label: string; unit: string; tooltip: string } = {
    key: '',
    label: '',
    unit: '',
    tooltip: ''
  };

  constructor(
    private req: RequestService<any>,
    private keycloak: KeycloakService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.keycloak.isLoggedIn().then((res) => {
      this.loggedIn = res;
    });

    this.req.getDatasets().subscribe((res: any) => {
      if (res.soildata) {
        this.soilData = res.soildata;
        this.countries = Array.from(new Set(this.soilData.map((data: any) => data.country)));
        this.generateViolinPlotChart();
      }
    });
  }

  getManagementColor(management: string): string {
    switch (management) {
      case 'Organic': return 'rgba(75, 192, 192, 1)';
      case 'Conventional': return 'rgba(255, 99, 132, 1)';
      case 'Monoculture': return 'rgba(54, 162, 235, 1)';
      case 'Mixture': return 'rgba(255, 238, 88, 1)';
      default: return 'rgba(0, 0, 0, 1)';
    }
  }

  generateViolinPlotChart() {
    const datasets = this.managementTypes.map((management: string) => ({
      label: management,
      data: this.countries.map((country: string) => {
        const values = this.soilData
          .filter((data: { country: string; management: string }) =>
            data.country === country && data.management === management)
          .map((data: { [key: string]: any }) => data[this.selectedParameter.key])
          .filter((value: any) => value !== undefined && value !== null);
        return values;
      }),
      backgroundColor: this.getManagementColor(management),
    }));

    this.chartOptions = [{
      title: `Violin Plot of ${this.selectedParameter.label} by Management Type by Country`,
      tooltip: this.selectedParameter.tooltip,
      config: {
        type: 'violin',
        data: {
          labels: this.countries,
          datasets: datasets,
        },
        options: {
          responsive: true,
          scales: {
            x: {
              title: {
                display: true, text: 'Country',
                font: { size: 12, weight: 'bold' }
              },
              ticks: {
                font: { size: 16 }
              }
            },
            y: {
              title: {
                display: true,
                text: `${this.selectedParameter.label} (${this.selectedParameter.unit})`,
                font: { size: 12, weight: 'bold' }
              },
              ticks: {
                font: { size: 16 }
              }
            },
          },
        },
      },
    }];
    this.cdr.detectChanges();
  }

  onParameterChange(event: MatSelectChange) {
    const selectedKey = event.value;
    const selectedParam = this.parameters.find(param => param.key === selectedKey);

    if (selectedParam) {
      this.selectedParameter.key = selectedParam.key;
      this.selectedParameter.label = selectedParam.label;
      this.selectedParameter.unit = selectedParam.unit;
      this.selectedParameter.tooltip = selectedParam.tooltip;
    }
    this.generateViolinPlotChart();
  }
}